import { useState, useEffect } from "react";
import { TextField, Typography, Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import axiosInstance from "../utils/axiosConfig";
import Button from "../Input/Button";

/**
 * ConfirmPasswordReset Component
 * 
 * This component handles the password reset confirmation step for child users.
 * It is accessed via a link sent to the parent's email containing a secure token.
 * 
 * URL STRUCTURE:
 * /reset-password/confirm?token=abc123...
 * 
 * WORKFLOW:
 * 1. Component extracts token from URL query parameters
 * 2. Parent/child enters which child's username to reset
 * 3. Parent/child enters new password twice (confirmation)
 * 4. Client-side validation ensures passwords match
 * 5. Submits to /api/auth/password-reset-confirm
 * 6. On success: Shows success message, redirects to WIX login after 3 seconds
 * 7. On error: Shows error, suggests requesting new reset if token expired
 * 
 * USERNAME FIELD:
 * Required because a parent may have multiple children, and we need to know
 * which child's password to reset. The parent email sent to them lists all
 * children's usernames for reference.
 * 
 * VALIDATION:
 * - Username required (which child to reset)
 * - Password minimum length: 8 characters (client-side)
 * - Password confirmation must match (client-side)
 * - Backend validates username belongs to parent (security)
 * - Backend enforces additional password rules via PasswordValidationRules trait
 * 
 * SECURITY:
 * - Token validated by backend (expiry check, single-use)
 * - Username validated to belong to parent's children
 * - Password hashed with bcrypt before storage
 * - Clear error messages guide user without revealing sensitive information
 * 
 * STYLING:
 * - Matches ProductionRegistration.jsx and ResetPasswordRequest.jsx
 * - BridgePal orange/green color scheme
 * - Responsive design with consistent footer
 */

const useStyles = makeStyles((theme) => ({
    root: {
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundImage: 'linear-gradient(to bottom, #3f51b5, #ffffff)',
        overflow: 'hidden', // Prevent scrolling
    },
    contentArea: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        padding: theme.spacing(1, 0),
    },
    registrationContainer: {
        width: '480px',
        maxWidth: '85%',
        backgroundColor: '#FF6419', // BridgePal orange
        boxShadow: theme.shadows[5],
        padding: theme.spacing(1.5, 3.5, 1.5),
        borderRadius: 12,
        outline: 'none',
    },
    title: {
        color: '#B4FA64', // BridgePal green
        textAlign: 'center',
        padding: '12px 24px',
        fontSize: 30,
        fontFamily: 'NeueHassDisplayMedium',
    },
    input: {
        '& .MuiOutlinedInput-root': {
            background: 'white',
            fontFamily: 'NeueHassDisplayMedium',
            borderRadius: 16,
        },
    },
    submitButton: {
        width: '80%',
        textTransform: 'capitalize',
        backgroundColor: '#B4FA64',
        color: '#FF6419',
        borderRadius: '24px',
        fontFamily: 'NeueHassDisplayRoman',
        '& .MuiButton-label': {
            fontSize: '19px'
        }
    },
    helperText: {
        color: '#B4FA64',
        fontSize: '14px',
        marginTop: '8px',
        textAlign: 'center',
    },
    successText: {
        color: '#B4FA64',
        fontSize: '18px',
        marginTop: '20px',
        textAlign: 'center',
        lineHeight: '1.6',
    },
    footer: {
        backgroundColor: '#b4fa64',
        width: '100%',
        padding: theme.spacing(2.5),
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        alignItems: 'center',
    },
    footerLink: {
        color: '#ff6419',
        textDecoration: 'underline',
        marginRight: theme.spacing(2.5),
        marginLeft: theme.spacing(2.5),
        marginBottom: theme.spacing(1),
        fontSize: '24px',
        fontWeight: 900,
        fontFamily: '"Lato", "NeueHaasGroteskDisplay Pro", Arial, sans-serif',
        '&:hover': {
            textDecoration: 'underline'
        }
    },
}));

export default function ConfirmPasswordReset() {
    const classes = useStyles();
    const { register, handleSubmit, watch, formState: { errors } } = useForm();
    const location = useLocation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [token, setToken] = useState('');
    
    /**
     * Extract token from URL on component mount
     * 
     * Token is passed as a query parameter: ?token=abc123...
     * If no token is found, redirect back to WIX website
     */
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const urlToken = params.get('token');
        
        if (!urlToken) {
            alert('Invalid reset link. Please request a new password reset.');
            window.location.href = 'https://www.bridgepalkids.com/';
        } else {
            setToken(urlToken);
            console.log('Password reset token extracted from URL');
        }
    }, [location]);
    
    // Watch password field for confirmation matching
    const password = watch("password", "");
    
    /**
     * Handle form submission
     * 
     * Sends new password and token to backend for validation and update.
     * On success: Shows success message, redirects to WIX login
     * On error: Shows error message, may redirect to request new reset
     */
    const onSubmit = async (data) => {
        setIsSubmitting(true);
        
        try {
            const response = await axiosInstance.post('/auth/password-reset-confirm', {
                token: token,
                username: data.username,
                password: data.password,
                password_confirmation: data.password_confirmation
            });
            
            if (response.status === 200) {
                console.log('Password reset successful');
                setSubmitSuccess(true);
                
                // Redirect to WIX login after 3 seconds
                setTimeout(() => {
                    window.location.href = 'https://www.bridgepalkids.com/';
                }, 3000);
            }
        } catch (error) {
            console.error('Password reset error:', error);
            
            const errorMessage = error.response?.data?.message || 'An error occurred. Please try again.';
            alert(errorMessage);
            
            // If token is invalid/expired, user might want to request a new reset
            // Show more helpful guidance
            if (error.response?.status === 400) {
                const shouldRequestNew = window.confirm(
                    'This reset link may have expired or already been used. Would you like to request a new password reset?'
                );
                
                if (shouldRequestNew) {
                    window.location.href = '/reset-password';
                }
            }
        } finally {
            setIsSubmitting(false);
        }
    };
    
    /**
     * Success state view
     * 
     * Displayed after password has been successfully reset.
     * Auto-redirects to WIX login page.
     */
    if (submitSuccess) {
        return (
            <div className={classes.root}>
                <div className={classes.contentArea}>
                    <div className={classes.registrationContainer}>
                        <Typography variant="h5" className={classes.title}>
                            Password Reset Complete!
                        </Typography>
                        <Typography className={classes.successText}>
                            Your password has been successfully reset.
                        </Typography>
                        <Typography className={classes.successText}>
                            You can now log in with your new password.
                        </Typography>
                        <Typography className={classes.helperText} style={{ marginTop: '20px', fontSize: '12px' }}>
                            Redirecting you to login...
                        </Typography>
                    </div>
                </div>
                <footer className={classes.footer}>
                    <a href="https://xcbrgpal.com" className={classes.footerLink}>BridgePal Website</a>
                    <a href="https://bridgepalkids.com/terms" className={classes.footerLink}>Terms of Use</a>
                    <a href="https://bridgepalkids.com/privacy-policy" className={classes.footerLink}>Privacy Policy</a>
                    <a href="https://bridgepalkids.com/kids-privacy-policy" className={classes.footerLink}>Kids Privacy</a>
                </footer>
            </div>
        );
    }
    
    /**
     * Main password reset form view
     * 
     * Three fields with client-side validation:
     * - Username (which child's password to reset)
     * - New password (minimum 8 characters)
     * - Confirm password (must match)
     */
    return (
        <div className={classes.root}>
            <div className={classes.contentArea}>
                <div className={classes.registrationContainer}>
                    <Typography variant="h5" className={classes.title}>
                        Create New Password
                    </Typography>
                    
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    placeholder="Child's Username"
                                    variant="outlined"
                                    fullWidth
                                    {...register("username", { required: "Username is required" })}
                                    className={classes.input}
                                    error={!!errors.username}
                                    helperText={errors.username?.message}
                                />
                            </Grid>
                            
                            <Grid item xs={12}>
                                <TextField
                                    placeholder="New Password"
                                    variant="outlined"
                                    fullWidth
                                    type="password"
                                    {...register("password", { 
                                        required: "Password is required",
                                        minLength: {
                                            value: 8,
                                            message: "Password must be at least 8 characters"
                                        }
                                    })}
                                    className={classes.input}
                                    error={!!errors.password}
                                    helperText={errors.password?.message}
                                />
                            </Grid>
                            
                            <Grid item xs={12}>
                                <TextField
                                    placeholder="Confirm New Password"
                                    variant="outlined"
                                    fullWidth
                                    type="password"
                                    {...register("password_confirmation", { 
                                        required: "Please confirm your password",
                                        validate: value => 
                                            value === password || "Passwords do not match"
                                    })}
                                    className={classes.input}
                                    error={!!errors.password_confirmation}
                                    helperText={errors.password_confirmation?.message}
                                />
                            </Grid>
                            
                            <Grid item xs={12}>
                                <Typography className={classes.helperText} style={{ color: '#ffffff', fontSize: '16px' }}>
                                    Enter the username for the child account you want to reset (check the email for the list of usernames). 
                                    Password must be at least 8 characters long.
                                </Typography>
                            </Grid>
                            
                            <Grid item xs={12} style={{ textAlign: 'center' }}>
                                <Button
                                    className={classes.submitButton}
                                    type="submit"
                                    disabled={isSubmitting || !token}
                                    onClick={handleSubmit(onSubmit)}
                                >
                                    {isSubmitting ? 'Resetting...' : 'Reset Password'}
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                </div>
            </div>
            
            <footer className={classes.footer}>
                <a href="https://xcbrgpal.com" className={classes.footerLink}>BridgePal Website</a>
                <a href="https://bridgepalkids.com/terms" className={classes.footerLink}>Terms of Use</a>
                <a href="https://bridgepalkids.com/privacy-policy" className={classes.footerLink}>Privacy Policy</a>
                <a href="https://bridgepalkids.com/kids-privacy-policy" className={classes.footerLink}>Kids Privacy</a>
            </footer>
        </div>
    );
}
